/**
 * ErrorBoundary do Sentry para capturar erros do React
 */

import React, { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Alert, Button, Card, CardBody, CardHeader, CardTitle } from 'reactstrap'
import { CHUNK_LOAD_ERROR } from '../configs/sentryConfig'

const CHUNK_RELOAD_KEY = 'chunkReloadAt'

// Permite no máximo uma recarga automática a cada 10s (evita loop se o servidor estiver fora)
const canAutoReload = () => {
  try {
    if (Date.now() - Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) < 10000) return false
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

/**
 * Componente de fallback customizado para erros
 */
const ErrorFallback = ({ error, resetError, componentStack }) => {
  // Aba aberta com versão antiga após deploy: recarrega para pegar os arquivos novos
  const [shouldReload] = useState(() => CHUNK_LOAD_ERROR.test(error?.message || '') && canAutoReload())

  useEffect(() => {
    if (shouldReload) window.location.reload()
  }, [shouldReload])

  if (shouldReload) return null

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Card className="w-75">
        <CardHeader>
          <CardTitle tag="h4" className="text-danger">
            Algo deu errado
          </CardTitle>
        </CardHeader>
        <CardBody>
          <Alert color="danger">
            <h5 className="alert-heading">Erro na aplicação</h5>
            <p>Ocorreu um erro inesperado. Nossa equipe foi notificada.</p>
            {import.meta.env.DEV && (
              <details className="mt-2">
                <summary>Detalhes do erro (apenas em desenvolvimento)</summary>
                <pre className="mt-2" style={{ fontSize: '12px', overflow: 'auto' }}>
                  {error.toString()}
                  {componentStack && (
                    <>
                      {'\n\n'}
                      {componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}
          </Alert>
          <div className="d-flex gap-2 mt-3">
            <Button color="primary" onClick={() => window.location.reload()}>
              Recarregar página
            </Button>
            {resetError && (
              <Button color="secondary" onClick={resetError}>
                Tentar novamente
              </Button>
            )}
            <Button color="info" onClick={() => (window.location.href = '/')}>
              Ir para página inicial
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

/**
 * ErrorBoundary do Sentry com fallback customizado
 */
const SentryErrorBoundary = ({ children, fallback }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={fallback || ErrorFallback}
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag('errorBoundary', true)
        scope.setLevel('error')
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

export default SentryErrorBoundary

